<script>
import { levelColor } from './severity';

export default {
    name: 'ResultsTable',
    props: {
        page: { type: Object, required: true },
        loading: { type: Boolean, default: false },
    },
    emits: ['change-page', 'open-trace', 'open-issue'],
    data() {
        return { expanded: {} };
    },
    computed: {
        lastPage() {
            return Math.max(1, Math.ceil(this.page.total / this.page.per_page));
        },
    },
    methods: {
        levelColor,
        toggle(id) {
            this.expanded = { ...this.expanded, [id]: !this.expanded[id] };
        },
        formatTs(ts) {
            return ts ? ts.replace('T', ' ').replace(/\+.*$/, '') : '';
        },
        prettyContext(context) {
            return JSON.stringify(context, null, 2);
        },
    },
};
</script>

<template>
    <div class="logs-results" style="margin-top: 10px">
        <p class="text-muted">
            <strong>{{ page.total }}</strong> results
        </p>

        <div class="table-responsive">
            <table class="table table-condensed table-hover logs-results-table">
                <thead>
                    <tr>
                        <th style="width: 24px"></th>
                        <th style="width: 90px">Severity</th>
                        <th style="width: 165px">Time (UTC)</th>
                        <th style="width: 110px">Channel</th>
                        <th>Summary</th>
                        <th style="width: 80px">ms</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="entry in page.entries" :key="entry.id">
                        <tr class="logs-row" @click="toggle(entry.id)">
                            <td>
                                <i class="fa" :class="expanded[entry.id] ? 'fa-caret-down' : 'fa-caret-right'"></i>
                            </td>
                            <td>
                                <span
                                    class="label"
                                    :style="{ background: levelColor(entry.level) }"
                                >{{ entry.level }}</span>
                            </td>
                            <td class="text-muted">{{ formatTs(entry.ts_utc) }}</td>
                            <td><code v-if="entry.channel">{{ entry.channel }}</code></td>
                            <td class="logs-summary">
                                <span v-if="entry.caller_context" class="text-muted">[{{ entry.caller_context.split('\\').pop() }}]</span>
                                {{ entry.span_name || entry.message }}
                            </td>
                            <td class="text-muted">{{ entry.duration_ms != null ? entry.duration_ms : '' }}</td>
                        </tr>
                        <tr v-if="expanded[entry.id]" class="logs-detail">
                            <td></td>
                            <td colspan="5">
                                <div class="row">
                                    <div class="col-md-8">
                                        <pre v-if="entry.context" class="logs-context">{{ prettyContext(entry.context) }}</pre>
                                        <p v-else class="text-muted">No context payload.</p>
                                    </div>
                                    <div class="col-md-4">
                                        <table class="table table-condensed">
                                            <tbody>
                                                <tr v-if="entry.exception_class">
                                                    <th>Exception</th>
                                                    <td><code>{{ entry.exception_class }}</code></td>
                                                </tr>
                                                <tr v-if="entry.fingerprint">
                                                    <th>Fingerprint</th>
                                                    <td>
                                                        <a href="#" @click.prevent="$emit('open-issue', entry.fingerprint)">
                                                            <code>{{ entry.fingerprint.slice(0, 12) }}…</code>
                                                        </a>
                                                    </td>
                                                </tr>
                                                <tr v-if="entry.correlation_id">
                                                    <th>Correlation</th>
                                                    <td>
                                                        <a href="#" @click.prevent="$emit('open-trace', entry.correlation_id)">
                                                            <code>{{ entry.correlation_id }}</code>
                                                        </a>
                                                    </td>
                                                </tr>
                                                <tr v-if="entry.saga_step">
                                                    <th>Saga step</th>
                                                    <td>
                                                        <code>{{ entry.saga_step }}</code>
                                                        <span v-if="entry.attempt" class="text-muted">(attempt {{ entry.attempt }})</span>
                                                    </td>
                                                </tr>
                                                <tr v-if="entry.request_id">
                                                    <th>Request</th>
                                                    <td><code>{{ entry.request_id }}</code></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <tr v-if="!page.entries.length && !loading">
                        <td colspan="6" class="text-center text-muted" style="padding: 30px">
                            No log entries match the current query.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="lastPage > 1" class="text-center">
            <ul class="pagination pagination-sm">
                <li :class="{ disabled: page.page <= 1 }">
                    <a href="#" @click.prevent="page.page > 1 && $emit('change-page', page.page - 1)">&laquo;</a>
                </li>
                <li class="active"><a href="#">{{ page.page }} / {{ lastPage }}</a></li>
                <li :class="{ disabled: page.page >= lastPage }">
                    <a href="#" @click.prevent="page.page < lastPage && $emit('change-page', page.page + 1)">&raquo;</a>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
.logs-row {
    cursor: pointer;
}
.logs-summary {
    font-family: Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    word-break: break-word;
}
.logs-context {
    max-height: 320px;
    overflow: auto;
    font-size: 12px;
}
.logs-detail > td {
    background: #f9f9f9;
}
</style>
